import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, Plus, Minus, Link, Image } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductModal = ({ isOpen, onClose, product = null, onProductSaved }) => {
  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState({});
  const fileInputRefs = useRef([]);
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    category: 'basketball',
    price: '',
    description: '',
    image_urls: [''],
    is_featured: false,
    is_new_arrival: false,
    is_spotlight: false,
    low_stock_threshold: 5,
    variants: [{ size: '', color: '', stock_quantity: '', sku: '' }]
  });

  const [categories, setCategories] = useState([
    { value: 'basketball', label: 'Basketball' },
    { value: 'running', label: 'Running' },
    { value: 'lifestyle', label: 'Lifestyle' },
    { value: 'limited_edition', label: 'Limited Edition' }
  ]);
  
  const [newCategory, setNewCategory] = useState('');
  const [showAddCategory, setShowAddCategory] = useState(false);

  const sizes = {
    toddler: ['2T', '3T', '4T', '5T', '6T', '7T', '8T', '9T', '10T'],
    preschool: ['10.5C', '11C', '11.5C', '12C', '12.5C', '13C', '13.5C', '1Y', '1.5Y', '2Y', '2.5Y', '3Y'],
    kids: ['3.5Y', '4Y', '4.5Y', '5Y', '5.5Y', '6Y', '6.5Y', '7Y'],
    men: ['6', '6.5', '7', '7.5', '8', '8.5', '9', '9.5', '10', '10.5', '11', '11.5', '12', '13', '14', '15', '16', '17', '18'],
    women: ['5W', '5.5W', '6W', '6.5W', '7W', '7.5W', '8W', '8.5W', '9W', '9.5W', '10W', '10.5W', '11W', '11.5W', '12W', '13W', '14W']
  };

  useEffect(() => {
    if (product) {
      const imageUrls = product.image_urls || [''];
      setFormData({
        name: product.name || '',
        brand: product.brand || '',
        category: product.category || 'basketball',
        price: product.price?.toString() || '',
        description: product.description || '',
        image_urls: imageUrls,
        is_featured: product.is_featured || false,
        is_new_arrival: product.is_new_arrival || false,
        is_spotlight: product.is_spotlight || false,
        low_stock_threshold: product.low_stock_threshold || 5,
        variants: product.variants || [{ size: '', color: '', stock_quantity: '', sku: '' }]
      });
      // Initialize file input refs for existing images
      fileInputRefs.current = imageUrls.map(() => React.createRef());
    } else {
      // Reset form for new product
      setFormData({
        name: '',
        brand: '',
        category: 'basketball',
        price: '',
        description: '',
        image_urls: [''],
        is_featured: false,
        is_new_arrival: false,
        is_spotlight: false,
        low_stock_threshold: 5,
        variants: [{ size: '', color: '', stock_quantity: '', sku: '' }]
      });
      // Initialize file input refs for new product
      fileInputRefs.current = [React.createRef()];
    }
  }, [product, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddCategory = () => {
    if (!newCategory.trim()) {
      toast.error('Category name cannot be empty');
      return;
    }

    // Convert to lowercase with underscores for value
    const categoryValue = newCategory.toLowerCase().replace(/\s+/g, '_');
    
    // Check if category already exists
    if (categories.some(cat => cat.value === categoryValue)) {
      toast.error('This category already exists');
      return;
    }

    // Add new category
    const newCat = {
      value: categoryValue,
      label: newCategory.trim()
    };
    
    setCategories(prev => [...prev, newCat]);
    setFormData(prev => ({ ...prev, category: categoryValue }));
    setNewCategory('');
    setShowAddCategory(false);
    toast.success('Category added successfully');
  };

  const handleImageUrlChange = (index, value) => {
    const newImageUrls = [...formData.image_urls];
    newImageUrls[index] = value;
    setFormData(prev => ({ ...prev, image_urls: newImageUrls }));
  };

  const addImageUrl = () => {
    setFormData(prev => ({
      ...prev,
      image_urls: [...prev.image_urls, '']
    }));
    // Add a new file input ref
    fileInputRefs.current.push(React.createRef());
  };

  const removeImageUrl = (index) => {
    if (formData.image_urls.length > 1) {
      const newImageUrls = formData.image_urls.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, image_urls: newImageUrls }));
      // Remove the corresponding file input ref
      fileInputRefs.current.splice(index, 1);
    }
  };

  const handleFileUpload = async (index, file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    try {
      setUploadingImages(prev => ({ ...prev, [index]: true }));

      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/upload-image`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data && response.data.url) {
        handleImageUrlChange(index, response.data.url);
        toast.success('Image uploaded to Cloudinary!');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImages(prev => ({ ...prev, [index]: false }));
    }
  };

  const handleMultipleFileUpload = async (files) => {
    // Validate files
    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not a valid image file`);
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 10MB)`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    // Limit total images
    const currentValidUrls = formData.image_urls.filter(url => url.trim() !== '');
    if (currentValidUrls.length + validFiles.length > 10) {
      toast.error('Maximum 10 images allowed per product');
      return;
    }

    try {
      setUploadingImages(prev => ({ ...prev, multiple: true }));

      const formDataUpload = new FormData();
      validFiles.forEach(file => {
        formDataUpload.append('files', file);
      });

      const response = await axios.post(`${API}/upload-multiple-images`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Handle new Cloudinary response format
      if (response.data.uploaded && response.data.uploaded.length > 0) {
        const newUrls = response.data.uploaded.map(result => result.url);
        
        // Add new URLs to existing ones, removing any empty ones first
        const existingUrls = formData.image_urls.filter(url => url.trim() !== '');
        const allUrls = [...existingUrls, ...newUrls];
        
        setFormData(prev => ({ ...prev, image_urls: allUrls }));
        
        toast.success(`${response.data.total_uploaded} image(s) uploaded to Cloudinary!`);
      }
        
      // Show errors for any failed uploads
      if (response.data.failed && response.data.failed.length > 0) {
        response.data.failed.forEach(result => {
          toast.error(`Failed to upload ${result.filename}: ${result.error}`);
        });
      }
    } catch (error) {
      console.error('Error uploading multiple images:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload images');
    } finally {
      setUploadingImages(prev => ({ ...prev, multiple: false }));
    }
  };

  const triggerFileInput = (index) => {
    if (fileInputRefs.current[index]?.current) {
      fileInputRefs.current[index].current.click();
    }
  };

  const handleVariantChange = (index, field, value) => {
    const newVariants = [...formData.variants];
    newVariants[index] = { ...newVariants[index], [field]: value };
    
    // Auto-generate SKU if brand and name are available
    if (field === 'size' || field === 'color') {
      const brand = formData.brand.substring(0, 2).toUpperCase();
      const name = formData.name.substring(0, 2).toUpperCase();
      const size = newVariants[index].size;
      const color = newVariants[index].color.substring(0, 1).toUpperCase();
      newVariants[index].sku = `${brand}${name}-${size}${color}`;
    }
    
    setFormData(prev => ({ ...prev, variants: newVariants }));
  };

  const addVariant = () => {
    setFormData(prev => ({
      ...prev,
      variants: [...prev.variants, { size: '', color: '', stock_quantity: '', sku: '' }]
    }));
  };

  const removeVariant = (index) => {
    if (formData.variants.length > 1) {
      const newVariants = formData.variants.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, variants: newVariants }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate required fields
      if (!formData.name || !formData.brand || !formData.price) {
        toast.error('Please fill in all required fields');
        setLoading(false);
        return;
      }

      // Validate variants
      for (let variant of formData.variants) {
        if (!variant.color || !variant.stock_quantity || !variant.sku) {
          toast.error('Please complete all variant information');
          setLoading(false);
          return;
        }
      }

      // Filter out empty image URLs
      const validImageUrls = formData.image_urls.filter(url => url.trim() !== '');
      if (validImageUrls.length === 0) {
        toast.error('Please provide at least one image URL');
        setLoading(false);
        return;
      }

      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        image_urls: validImageUrls,
        variants: formData.variants.map(variant => ({
          ...variant,
          stock_quantity: parseInt(variant.stock_quantity)
        }))
      };

      if (product) {
        // Update existing product
        await axios.put(`${API}/products/${product.id}`, productData);
        toast.success('Product updated successfully');
      } else {
        // Create new product
        await axios.post(`${API}/products`, productData);
        toast.success('Product created successfully');
      }

      onProductSaved();
      onClose();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {product ? 'Edit Product' : 'Add New Product'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            data-testid="close-product-modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                placeholder="Enter product name"
                required
                data-testid="product-name-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Brand *
              </label>
              <input
                type="text"
                value={formData.brand}
                onChange={(e) => handleInputChange('brand', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                placeholder="Enter brand name"
                required
                data-testid="product-brand-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category *
              </label>
              <select
                value={formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                data-testid="product-category-select"
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
              
              {/* Add Category Button */}
              {!showAddCategory && (
                <button
                  type="button"
                  onClick={() => setShowAddCategory(true)}
                  className="mt-2 text-sm text-brand-purple hover:text-brand-purple-dark font-medium flex items-center"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add New Category
                </button>
              )}

              {/* Add Category Input */}
              {showAddCategory && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    New Category Name
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                      placeholder="e.g., Skateboarding"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-purple"
                    />
                    <button
                      type="button"
                      onClick={handleAddCategory}
                      className="px-3 py-2 bg-brand-purple text-white rounded-lg text-sm font-medium hover:bg-brand-purple-dark transition-colors"
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddCategory(false);
                        setNewCategory('');
                      }}
                      className="px-3 py-2 bg-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Price *
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => handleInputChange('price', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                placeholder="0.00"
                required
                data-testid="product-price-input"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
              placeholder="Enter product description"
              data-testid="product-description-input"
            />
          </div>

          {/* Image Upload Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-4">
              Product Images *
            </label>
            
            {/* Image Previews */}
            {formData.image_urls.length > 0 && formData.image_urls.some(url => url.trim() !== '') && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-600 mb-2">Current Images:</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {formData.image_urls.map((url, index) => {
                    if (!url.trim()) return null;
                    return (
                      <div key={index} className="relative group">
                        <img
                          src={url}
                          alt={`Product ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg border border-gray-300"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/150x150?text=Image+Error';
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => removeImageUrl(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* File Upload Area */}
            <div className="space-y-4">
              {/* Drag and Drop Area */}
              <div 
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors"
                onDragOver={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.add('border-blue-400', 'bg-blue-50');
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-blue-400', 'bg-blue-50');
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-blue-400', 'bg-blue-50');
                  const files = Array.from(e.dataTransfer.files).filter(file => file.type.startsWith('image/'));
                  if (files.length > 0) {
                    handleMultipleFileUpload(files);
                  }
                }}
              >
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-lg font-medium text-gray-900 mb-2">Drop images here</p>
                <p className="text-sm text-gray-500 mb-4">or click to browse files</p>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => {
                    const files = Array.from(e.target.files);
                    if (files.length > 0) {
                      handleMultipleFileUpload(files);
                    }
                  }}
                  className="hidden"
                  id="multiple-file-upload"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('multiple-file-upload').click()}
                  className="mb-2"
                >
                  <Image className="w-4 h-4 mr-2" />
                  Select Images
                </Button>
                <p className="text-xs text-gray-400">
                  Supports: JPG, PNG, GIF, WEBP (Max 10MB each, up to 10 images)
                </p>
              </div>

              {/* Manual URL Input */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-600">Or add image URLs manually:</h4>
                  <button
                    type="button"
                    onClick={addImageUrl}
                    className="flex items-center text-blue-600 hover:text-blue-800 text-sm"
                    data-testid="add-image-url"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add URL
                  </button>
                </div>
                <div className="space-y-2">
                  {formData.image_urls.map((url, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="url"
                        value={url}
                        onChange={(e) => handleImageUrlChange(index, e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-500 text-sm"
                        placeholder="https://example.com/image.jpg"
                        data-testid={`image-url-${index}`}
                      />
                      {formData.image_urls.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeImageUrl(index)}
                          className="p-1 text-red-500 hover:text-red-700"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Upload Progress */}
            {Object.values(uploadingImages).some(val => val === true) && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <h4 className="text-sm font-medium text-blue-900 mb-2">Uploading Images...</h4>
                <div className="space-y-2">
                  {Object.entries(uploadingImages).map(([key, isUploading]) => {
                    if (!isUploading) return null;
                    return (
                      <div key={key} className="flex items-center space-x-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        <span className="text-sm text-blue-700">Processing image...</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="featured"
                  checked={formData.is_featured}
                  onChange={(e) => handleInputChange('is_featured', e.target.checked)}
                  className="w-5 h-5 text-gray-900 border-gray-300 rounded focus:ring-gray-500"
                  data-testid="featured-checkbox"
                />
                <label htmlFor="featured" className="text-sm font-medium text-gray-700">
                  Featured Product
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="new-arrival"
                  checked={formData.is_new_arrival}
                  onChange={(e) => handleInputChange('is_new_arrival', e.target.checked)}
                  className="w-5 h-5 text-gray-900 border-gray-300 rounded focus:ring-gray-500"
                  data-testid="new-arrival-checkbox"
                />
                <label htmlFor="new-arrival" className="text-sm font-medium text-gray-700">
                  New Arrival
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="spotlight"
                  checked={formData.is_spotlight}
                  onChange={(e) => handleInputChange('is_spotlight', e.target.checked)}
                  className="w-5 h-5 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500"
                  data-testid="spotlight-checkbox"
                />
                <label htmlFor="spotlight" className="text-sm font-medium text-gray-700">
                  <span className="flex items-center">
                    <span className="text-yellow-600 mr-1">⭐</span>
                    Spotlight Product
                    <span className="ml-2 text-xs text-gray-500">(Only 1 allowed)</span>
                  </span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Low Stock Threshold
              </label>
              <input
                type="number"
                value={formData.low_stock_threshold}
                onChange={(e) => handleInputChange('low_stock_threshold', parseInt(e.target.value))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                min="1"
                data-testid="stock-threshold-input"
              />
            </div>
          </div>

          {/* Variants */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Product Variants *
              </label>
              <button
                type="button"
                onClick={addVariant}
                className="flex items-center text-blue-600 hover:text-blue-800"
                data-testid="add-variant"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Variant
              </button>
            </div>

            <div className="space-y-4">
              {formData.variants.map((variant, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-5 gap-4 p-4 border border-gray-200 rounded-lg">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Size</label>
                    <select
                      value={variant.size}
                      onChange={(e) => handleVariantChange(index, 'size', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-500"
                      data-testid={`variant-size-${index}`}
                    >
                      <option value="">Select Size</option>
                      <optgroup label="👶 Toddler">
                        {sizes.toddler.map(size => (
                          <option key={size} value={size}>US {size}</option>
                        ))}
                      </optgroup>
                      <optgroup label="👦 Preschool">
                        {sizes.preschool.map(size => (
                          <option key={size} value={size}>US {size}</option>
                        ))}
                      </optgroup>
                      <optgroup label="🧒 Kids">
                        {sizes.kids.map(size => (
                          <option key={size} value={size}>US {size}</option>
                        ))}
                      </optgroup>
                      <optgroup label="👨 Men's">
                        {sizes.men.map(size => (
                          <option key={size} value={size}>US {size}</option>
                        ))}
                      </optgroup>
                      <optgroup label="👩 Women's">
                        {sizes.women.map(size => (
                          <option key={size} value={size}>US {size}</option>
                        ))}
                      </optgroup>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Color</label>
                    <input
                      type="text"
                      value={variant.color}
                      onChange={(e) => handleVariantChange(index, 'color', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-500"
                      placeholder="Color"
                      data-testid={`variant-color-${index}`}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Stock</label>
                    <input
                      type="number"
                      value={variant.stock_quantity}
                      onChange={(e) => handleVariantChange(index, 'stock_quantity', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-500"
                      placeholder="0"
                      min="0"
                      data-testid={`variant-stock-${index}`}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">SKU</label>
                    <input
                      type="text"
                      value={variant.sku}
                      onChange={(e) => handleVariantChange(index, 'sku', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-500"
                      placeholder="SKU"
                      data-testid={`variant-sku-${index}`}
                    />
                  </div>

                  <div className="flex items-end">
                    {formData.variants.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeVariant(index)}
                        className="p-2 text-red-500 hover:text-red-700"
                        data-testid={`remove-variant-${index}`}
                      >
                        <Minus className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              data-testid="cancel-product"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-brand-purple hover:bg-brand-purple-dark text-white"
              data-testid="save-product"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                product ? 'Update Product' : 'Create Product'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductModal;